import { tick } from 'svelte';
import DataTable from 'datatables.net-bs5';

const dialogSelector = '[role="dialog"], dialog[open]';
const handledInputs = new WeakSet();

function isSearchInput(input) {
    if (!(input instanceof HTMLInputElement) || input.disabled || input.readOnly) return false;
    return input.matches('[data-dialog-search], [type="search"], thead input, .svelte-select input')
        || /search|filter/i.test(`${input.placeholder} ${input.getAttribute('aria-label') || ''} ${input.id}`);
}

function visible(element) {
    return element.isConnected && element.getClientRects().length > 0;
}

function consume(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    handledInputs.add(event.target);
}

async function chooseSingle(options, event) {
    if (event.key !== 'Enter' || event.isComposing || event.repeat || !isSearchInput(event.target)) return;
    if (options.busy?.()) {
        consume(event);
        return;
    }
    const results = options.results(event);
    if (results.length !== 1 || (options.total && options.total() !== 1)) return;
    consume(event);
    await options.select(results[0]);
    await tick();
    if (!options.busy?.()) await options.confirm?.();
}

// Explicit callbacks keep keyboard confirmation on the same path as a mouse selection.
export function dialogSearch(node, options) {
    let current = options;
    let pending = false;
    const handler = (event) => {
        if (event.key !== 'Enter' || event.isComposing || event.repeat || !isSearchInput(event.target)) return;
        if (pending) { consume(event); return; }
        pending = true;
        void chooseSingle(current, event)
            .catch((error) => console.error('Dialog selection failed', error))
            .finally(() => { pending = false; });
    };
    node.addEventListener('keydown', handler, true);
    return {
        update(value) { current = value; },
        destroy() { node.removeEventListener('keydown', handler, true); }
    };
}

function tableChoice(dialog, input) {
    const table = input.closest('table') || dialog.querySelector('table');
    if (!table || !DataTable.isDataTable(table)) return null;
    const api = new DataTable.Api(table);
    const settings = api.settings()[0];
    if (settings.bDrawing || !settings._bInitComplete) return null;
    // A pending server draw must not select a row from the previous response.
    if (settings.oFeatures.bServerSide && Number(api.ajax.json()?.draw) !== settings.iDraw) return null;
    if (api.page.info().recordsDisplay !== 1) return null;
    const header = input.closest('th');
    if (header) {
        const column = api.column(`${header.cellIndex}:visible`);
        if (column.search() !== input.value) return null;
    }
    const row = api.rows({ page: 'current' }).nodes()[0];
    return row ? { row, data: api.row(row).data() } : null;
}

export function singleTableResult(input) {
    const dialog = input.closest(dialogSelector);
    const choice = dialog && tableChoice(dialog, input);
    return choice ? [choice] : [];
}

export function installDialogSearch() {
    const focused = new WeakSet();
    let frame;
    let stopped = false;
    function focusSearches() {
        frame = null;
        if (stopped) return;
        const dialogs = [...document.querySelectorAll(dialogSelector)].filter(visible);
        const dialog = dialogs.at(-1);
        if (!dialog || focused.has(dialog)) return;
        const scope = dialog.querySelector('[data-dialog-search-scope]') || dialog;
        const preferred = scope.querySelector('[data-dialog-search]');
        const input = preferred || [...scope.querySelectorAll('input')].find((item) => isSearchInput(item) && visible(item));
        if (!input || !visible(input)) return;
        // Do not move focus again as results arrive or after the user chooses another control.
        focused.add(dialog);
        input.focus({ preventScroll: true });
    }
    function scheduleFocus() {
        if (frame == null) frame = requestAnimationFrame(focusSearches);
    }
    const observer = new MutationObserver((changes) => {
        if (changes.some((change) => [...change.addedNodes].some((node) =>
            node instanceof Element && (node.matches(`${dialogSelector}, input`) || node.querySelector(`${dialogSelector}, input`))
        ))) scheduleFocus();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    async function onKeydown(event) {
        if (event.key !== 'Enter' || event.isComposing || event.repeat || !isSearchInput(event.target)) return;
        const dialog = event.target.closest(dialogSelector);
        const confirm = dialog?.querySelector('[data-dialog-confirm]');
        if (!dialog || !confirm) return;
        const select = event.target.closest('.svelte-select');
        if (select) {
            const choices = [...select.querySelectorAll('.list-item')].filter((row) =>
                !row.querySelector('.list-group-title, .not-selectable') && visible(row));
            if (choices.length !== 1) return;
            consume(event);
            choices[0].click();
        } else {
            const choice = tableChoice(dialog, event.target);
            if (!choice) {
                if (event.target.closest('table') && DataTable.isDataTable(event.target.closest('table'))) consume(event);
                return;
            }
            consume(event);
            if (!choice.row.matches('.selected, .selected-row, [aria-selected="true"]')) {
                const checkbox = choice.row.querySelector('input[type="checkbox"]');
                if (checkbox) {
                    if (!checkbox.checked) checkbox.click();
                } else choice.row.click();
            }
        }
        await tick();
        if (dialog.isConnected && !confirm.disabled) confirm.click();
    }
    const confirming = new WeakSet();
    const keydown = (event) => {
        if (event.key !== 'Enter' || event.isComposing || event.repeat || !isSearchInput(event.target)) return;
        const input = event.target;
        if (confirming.has(input)) { consume(event); return; }
        confirming.add(input);
        void onKeydown(event)
            .catch((error) => console.error('Dialog selection failed', error))
            .finally(() => confirming.delete(input));
    };
    const keyup = (event) => {
        if (event.key === 'Enter' && handledInputs.has(event.target)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            handledInputs.delete(event.target);
        }
    };
    document.addEventListener('keydown', keydown, true);
    document.addEventListener('keyup', keyup, true);
    scheduleFocus();
    return () => {
        stopped = true;
        observer.disconnect();
        if (frame != null) cancelAnimationFrame(frame);
        document.removeEventListener('keydown', keydown, true);
        document.removeEventListener('keyup', keyup, true);
    };
}
